import { StatusChip } from '@dealstack/frontend/shared/components/Chips/CommonChips';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { memo, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { ISideTabItemProps } from './SideTabItem.types';

const NON_BOLD_FONT_STATES: ISideTabItemProps['state'][] = ['visited', 'inactive'];
const KEEP_CURRENT_STATE_ICON: ISideTabItemProps['state'][] = ['error', 'warning'];
const SideTabItem = ({
	className,
	children,
	state,
	currentState,
	status,
	onClick,
}: ISideTabItemProps) => {
	const stateIconVariant: PredefinedIcons = useMemo(() => {
		const stateIconVariant = KEEP_CURRENT_STATE_ICON.includes(currentState)
			? currentState
			: state;
		switch (stateIconVariant) {
			case 'active':
				return 'radio-checked';
			case 'visited':
				return `checkmark-circle-contained-filled`;
			case 'error':
				return `warning-filled`;
			case 'warning':
				return `information-filled`;
			default:
				return `radio-unchecked`;
		}
	}, [state]);

	return (
		<div onClick={onClick} className={className}>
			<StateIcon size="lg" variant={stateIconVariant} />
			<Text bold={NON_BOLD_FONT_STATES.includes(state) ? false : true} variant="button">
				{children}
			</Text>
			{!!status && (
				<StatusChipWrapper>
					<StatusChip iconSize="lg" variant={status} />
				</StatusChipWrapper>
			)}
		</div>
	);
};

const StateIcon = styled(Icon)`
	margin-right: 0.563rem;
`;
const StatusChipWrapper = styled.div`
	margin-left: auto;
	padding-left: 1em;
	${StatusChip} {
		padding: 0.15em 0.5em;
	}
`;

const Memoized = memo(SideTabItem);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	min-height: 1.3rem;

	/* overflow: hidden; */
	--background-color-inactive: ${({ theme }) => theme.v2.color.white[500]};

	background-color: transparent;

	border-radius: 4px 0 0 4px;
	border-left: 2px solid transparent;
	padding: 0.513rem 0.563rem;

	${Icon} {
		background-color: var(--background-color-inactive);
		border-radius: 50%;
		flex-shrink: 0;
		&.svg {
			--size: 1rem;
		}
	}

	${({ onClick }) =>
		onClick
			? css`
					cursor: pointer;
					&:hover {
						background-color: white;
						${StateIcon}.svg, ${Text} {
							color: unset;
						}

						${Icon} {
							background-color: white;
						}
					}
				`
			: ``}

	${({ theme, state, currentState }) => {
		const activeStateIconColorMapping = {
			warning: `${theme.v2.color.warning[1000]}`,
			error: `${theme.v2.color.error[800]}`,
		};

		switch (state) {
			case 'active':
				return css`
					background-color: ${theme.v2.color.white[500]};
					color: ${theme.v2.color.secondary[800]};
					${StateIcon}.svg {
						color: ${currentState && KEEP_CURRENT_STATE_ICON.includes(currentState)
							? activeStateIconColorMapping[currentState]
							: `${theme.color.greys[700]}`};
					}
				`;
			case 'inactive':
				return css`
					${Text} {
						color: ${theme.v2.color.neutral[1100]};
					}
				`;
			case 'visited':
				return css`
					${StateIcon}.svg, ${Text} {
						color: ${theme.v2.color.neutral[700]};
					}
				`;

			case 'error':
				return css`
					${StateIcon}.svg {
						color: ${theme.v2.color.error[800]};
					}
					${Text} {
						color: ${theme.v2.color.neutral[1100]};
					}
				`;

			case 'warning':
				return css`
					${StateIcon}.svg {
						color: ${theme.v2.color.warning[1000]};
					}
					${Text} {
						color: ${theme.v2.color.neutral[1100]};
					}
				`;

			default:
				return ``;
		}
	}};

	${({ theme, faded }) =>
		faded
			? css`
					background-color: var(--background-color-inactive);
					border-left-color: transparent;

					${Icon}.svg, ${Text} {
						color: ${theme.v2.color.neutral[500]};
					}
				`
			: ''}
`;

export { Styled as SideTabItem };
